"use client";

// ============================================================
// CLUBHUB – Mitglied anlegen / bearbeiten (Formularkoerper)
// ============================================================
// Derselbe Koerper im Dialog (Liste, Detailseite) und auf der Seite
// /mitglieder/neu (Befehlspalette). Gegliedert in die fuenf Fragen,
// die man beim Erfassen wirklich stellt: wer, wie erreichbar, wo,
// welche Mitgliedschaft, was darf der Verein. ClubDesk zeigt dafuer
// eine Wand aus 52 Feldern.
// ============================================================
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { DateInput } from "@/components/ui/date-input";
import { Switch } from "@/components/ui/switch";
import { DialogNotice, DialogRow } from "@/components/ui/dialog";
import { CONTACT_STATUS, GENDER, PAYMENT_METHOD, SALUTATION } from "@/lib/labels";
import { useMitgliederAuswahl } from "./auswahl-kontext";
import type { ContactView } from "@/lib/views";
import { cn, formatEuro } from "@/lib/utils";
import { FEE_INTERVAL } from "@/lib/labels";

const opt = <T extends string>(m: Record<T, string>) => Object.entries(m).map(([value, label]) => ({ value, label: label as string }));

function Abschnitt({ titel, hinweis, children }: { titel: string; hinweis?: string; children: React.ReactNode }) {
  return (
    <fieldset className="space-y-3">
      <legend className="text-overline text-text-tertiary mb-2">{titel}</legend>
      {hinweis && <p className="text-caption text-text-tertiary -mt-1">{hinweis}</p>}
      {children}
    </fieldset>
  );
}

export function MitgliedFormular({ kontakt, fehler }: { kontakt?: ContactView; fehler?: Record<string, string> }) {
  const [art, setArt] = useState<"PERSON" | "ORGANIZATION">(kontakt?.kind ?? "PERSON");
  const [status, setStatus] = useState<string>(kontakt?.status ?? "ACTIVE");
  const [zahlart, setZahlart] = useState<string>(kontakt?.paymentMethod ?? "SEPA");
  const [gruppen, setGruppen] = useState<string[]>(kontakt?.groups.map((g) => g.groupId) ?? []);
  const [newsletter, setNewsletter] = useState(kontakt?.consentNewsletter ?? false);
  const [fotos, setFotos] = useState(kontakt?.consentPhotos ?? false);
  const [internet, setInternet] = useState(kontakt?.consentInternet ?? false);
  const mitglied = ["ACTIVE", "PASSIVE", "HONORARY", "PROSPECT"].includes(status);
  const { beitragsarten, gruppen: alleGruppen } = useMitgliederAuswahl();

  return (
    <div className="space-y-6">
      <Abschnitt titel="Person">
        <div className="bg-bg-secondary inline-flex rounded-lg p-0.5">
          {(["PERSON", "ORGANIZATION"] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setArt(k)}
              className={cn(
                "text-body-sm rounded-md px-3 py-1.5 transition-colors",
                art === k ? "bg-bg-elevated text-text-primary shadow-e1 font-medium" : "text-text-secondary",
              )}
            >
              {k === "PERSON" ? "Person" : "Organisation"}
            </button>
          ))}
          <input type="hidden" name="kind" value={art} />
        </div>
        {art === "PERSON" ? (
          <>
            <DialogRow columns={3}>
              <Select name="salutation" label="Anrede" options={opt(SALUTATION).filter((o) => o.value !== "FIRMA")} defaultValue={kontakt?.salutation ?? ""} placeholder="Wählen" />
              <Input name="title" label="Titel" defaultValue={kontakt?.title ?? ""} placeholder="z. B. Dr." />
              <Select name="gender" label="Geschlecht" hint="Für die Bestandserhebung" options={opt(GENDER)} defaultValue={kontakt?.gender ?? ""} placeholder="Wählen" />
            </DialogRow>
            <DialogRow columns={2}>
              <Input name="firstName" label="Vorname" required defaultValue={kontakt?.firstName ?? ""} error={fehler?.firstName} />
              <Input name="lastName" label="Nachname" required defaultValue={kontakt?.lastName ?? ""} error={fehler?.lastName} />
            </DialogRow>
            <DialogRow columns={2}>
              <div>
                <label className="text-body-sm text-text-primary mb-1.5 block font-medium">Geburtsdatum</label>
                <DateInput name="birthDate" defaultValue={kontakt?.birthDate ?? ""} />
              </div>
              <div />
            </DialogRow>
          </>
        ) : (
          <Input name="organizationName" label="Name der Organisation" required defaultValue={kontakt?.organizationName ?? ""} error={fehler?.organizationName} />
        )}
      </Abschnitt>

      <Abschnitt titel="Erreichbarkeit">
        <DialogRow columns={2}>
          <Input name="email" type="email" label="E-Mail" defaultValue={kontakt?.email ?? ""} error={fehler?.email} hint="Darf mit Familienmitgliedern geteilt sein" />
          <Input name="mobile" type="tel" label="Mobil" defaultValue={kontakt?.mobile ?? ""} />
        </DialogRow>
        <DialogRow columns={2}>
          <Input name="phone" type="tel" label="Telefon" defaultValue={kontakt?.phone ?? ""} />
          <Select
            name="communication"
            label="Post bevorzugt per"
            options={[{ value: "EMAIL", label: "E-Mail" }, { value: "POST", label: "Brief" }]}
            defaultValue={kontakt?.communication ?? "EMAIL"}
          />
        </DialogRow>
      </Abschnitt>

      <Abschnitt titel="Anschrift">
        <Input name="street" label="Straße und Hausnummer" defaultValue={kontakt?.street ?? ""} />
        <DialogRow template="8rem 1fr 8rem">
          <Input name="zip" label="PLZ" inputMode="numeric" defaultValue={kontakt?.zip ?? ""} />
          <Input name="city" label="Ort" defaultValue={kontakt?.city ?? ""} />
          <Input name="country" label="Land" defaultValue={kontakt?.country ?? "DE"} />
        </DialogRow>
      </Abschnitt>

      <Abschnitt titel="Mitgliedschaft">
        <DialogRow columns={3}>
          <Select name="status" label="Status" options={Object.entries(CONTACT_STATUS).map(([value, s]) => ({ value, label: s.label }))} value={status} onChange={setStatus} />
          <Input name="memberNumber" label="Mitgliedsnummer" defaultValue={kontakt?.memberNumber ?? ""} placeholder="wird vergeben" className="tabular-nums" />
          <div>
            <label className="text-body-sm text-text-primary mb-1.5 block font-medium">Eintritt</label>
            <DateInput name="entryDate" defaultValue={kontakt?.entryDate ?? new Date().toISOString().slice(0, 10)} />
          </div>
        </DialogRow>
        {mitglied && (
          <DialogRow columns={2}>
            <Select
              name="feeTypeId"
              label="Beitrag"
              options={beitragsarten.map((f) => ({ value: f.id, label: f.name, hint: `${formatEuro(f.amount)} ${FEE_INTERVAL[f.interval]}` }))}
              defaultValue={kontakt?.feeTypeId ?? ""}
              placeholder="Beitragsart wählen"
            />
            <Select name="paymentMethod" label="Zahlart" options={opt(PAYMENT_METHOD)} value={zahlart} onChange={setZahlart} />
          </DialogRow>
        )}
        {mitglied && zahlart === "SEPA" && !kontakt?.hasMandate && (
          <DialogNotice tone="warning">
            Für die Lastschrift fehlt noch ein SEPA-Mandat. Nach dem Speichern im Register „Beiträge“ erfassen oder das
            Mitglied über den Aufnahmeantrag online unterschreiben lassen.
          </DialogNotice>
        )}
        <div>
          <p className="text-body-sm text-text-primary mb-1.5 font-medium">Gruppen</p>
          <div className="flex flex-wrap gap-1.5">
            {alleGruppen.map((g) => {
              const an = gruppen.includes(g.id);
              return (
                <button
                  key={g.id}
                  type="button"
                  aria-pressed={an}
                  onClick={() => setGruppen((x) => (an ? x.filter((y) => y !== g.id) : [...x, g.id]))}
                  className={cn(
                    "text-body-sm rounded-full px-2.5 py-1 ring-1 transition-colors ring-inset",
                    an ? "bg-accent-subtle text-accent ring-accent/30" : "text-text-secondary ring-border-control hover:bg-bg-secondary",
                  )}
                >
                  {g.name}
                </button>
              );
            })}
          </div>
          {gruppen.map((id) => (
            <input key={id} type="hidden" name="groupIds" value={id} />
          ))}
        </div>
      </Abschnitt>

      <Abschnitt titel="Einwilligungen" hinweis="Jede Einwilligung einzeln und jederzeit widerrufbar. Der Zeitpunkt wird festgehalten.">
        <div className="divide-border-subtle border-border-subtle divide-y rounded-lg border">
          <div className="p-3"><Switch checked={newsletter} onChange={setNewsletter} label="Newsletter und Werbung per E-Mail" hint="Reine Vereinsinformationen gehen auch ohne." /></div>
          <div className="p-3"><Switch checked={fotos} onChange={setFotos} label="Fotos bei Vereinsveranstaltungen" /></div>
          <div className="p-3"><Switch checked={internet} onChange={setInternet} label="Internet und Social Media" hint="Name und Bild auf der Vereinswebsite und in sozialen Kanälen." /></div>
        </div>
        <input type="hidden" name="consentNewsletter" value={String(newsletter)} />
        <input type="hidden" name="consentPhotos" value={String(fotos)} />
        <input type="hidden" name="consentInternet" value={String(internet)} />
      </Abschnitt>
    </div>
  );
}

/** Pruefung vor dem Speichern — dieselbe Regel prueft die Server Action. */
export { pruefeMitglied } from "@/lib/mitglieder/pruefung";
